/**
 * Vista compartida del panel "Finanzas del aseador".
 *
 * Misma UI/datos para dos usuarios distintos:
 *   - **aseador**: ve sus propias finanzas (`/finanzas-aseador`). El padre
 *     fetchea con `getMisFinanzasAseador()` (toma id del JWT).
 *   - **admin/superadmin**: ve las finanzas del aseador que seleccionó
 *     (`/finanzas-aseador-admin`). El padre fetchea con
 *     `getFinanzasAseadorById(id)`.
 *
 * El componente NO fetchea por sí mismo — recibe `meses` ya resueltos y un
 * callback `onReload`. Esto garantiza que el aseador y el admin vean
 * exactamente la misma cuadrícula numérica (la fórmula vive en el backend
 * `buildResumenFinanzasAseador`, ver `docs/modulo-finanzas-aseador.md`).
 *
 * Por qué un componente "vista pura" en vez de un hook compartido: la
 * autoridad del fetch cambia (JWT vs id seleccionable). Inyectar el data
 * desde el padre mantiene clara la responsabilidad: aquí solo se pinta.
 */

import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Empty,
  Popconfirm,
  Segmented,
  Spin,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  GiftOutlined,
  ReloadOutlined,
  UndoOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type {
  AseoFinanzaItem,
  MesFinanzaAseador,
  MovimientoAseador,
  MovimientoEstado,
} from "../../services/finanzas.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import ComprobantesAsignacion from "./ComprobantesAsignacion";
import "./finanzasAseador.css";
import { ahoraChile } from "../../utils/fechaChile";

// Setear locale de dayjs DESPUÉS de los imports — la regla `import/first`
// del ESLint de CRA falla el build si hay statements entre imports.
dayjs.locale("es");

const { Title, Text } = Typography;

const fmtClp = (n: number): string =>
  `$${Math.round(n).toLocaleString("es-CL")}`;

const fmtFecha = (iso: string | null): string => {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  const [y, m, day] = d.split("-");
  if (!y || !m || !day) return iso;
  return `${day}-${m}-${y}`;
};

/**
 * Capitaliza solo la primera letra del label de mes que llega del backend
 * (ej. "abril de 2026" → "Abril de 2026"). En español neutro las
 * preposiciones y artículos van en minúscula; el `textTransform: capitalize`
 * de CSS rompía esto al capitalizar cada palabra ("Abril De 2026"), que es
 * incorrecto y se veía mal.
 */
const capitalizarMes = (label: string | null | undefined): string => {
  if (!label) return "";
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const ESTADO_TAG: Record<
  AseoFinanzaItem["estado"],
  { color: string; label: string }
> = {
  pendiente: { color: "default", label: "Agendado" },
  en_proceso: { color: "blue", label: "En proceso" },
  completado: { color: "gold", label: "Completado (por pagar)" },
  pagado: { color: "green", label: "Pagado" },
};

const MOV_ESTADO_TAG: Record<
  MovimientoEstado,
  { color: string; label: string }
> = {
  pendiente: { color: "gold", label: "Pendiente" },
  pagado: { color: "green", label: "Aprobado" },
  denegado: { color: "red", label: "Denegado" },
};

/**
 * Tabla de movimientos extra (Fase 2). Hereda los mismos colores y
 * conceptos que la sección de aseos para que la lectura mental sea fluida.
 *
 * Si `onApprove`/`onDeny` están seteados (modo admin), aparece la columna
 * "Acciones" con botones para aprobar/denegar cada fila pendiente. En la
 * vista del aseador esas props son `undefined` y la columna no aparece.
 */
function MovimientosTable({
  movimientos,
  onApprove,
  onDeny,
  onRevert,
  onEdit,
  onDelete,
}: {
  movimientos: MovimientoAseador[];
  // Reciben el movimiento completo (round 2026-07-27): una ocurrencia de flujo
  // REGULAR no se identifica con su id —que se repite entre meses— sino con el
  // par (id, período), y el caller necesita ambos para rutear la acción.
  onApprove?: (mov: MovimientoAseador) => void;
  onDeny?: (mov: MovimientoAseador) => void;
  onRevert?: (mov: MovimientoAseador) => void;
  onEdit?: (mov: MovimientoAseador) => void;
  // C5 (D-D): borrar SOLO movimientos pendientes o denegados.
  onDelete?: (id: number) => void;
}) {
  const isAdmin = !!(onApprove || onDeny || onRevert || onEdit || onDelete);

  // Las filas con `signo` ±1 las pintamos con `+`/`−` y color verde/rojo.
  const renderMonto = (m: MovimientoAseador) => {
    const prefijo = m.signo === 1 ? "+" : "-";
    const color = m.signo === 1 ? "#16a34a" : "#dc2626";
    return (
      <strong style={{ color }}>
        {prefijo}
        {fmtClp(m.monto)}
      </strong>
    );
  };

  // Renderiza la "contraparte" del movimiento — quién está al otro lado
  // del flujo del aseador. Round 2026-05-06:
  //  - signo +1 (recibió): "de [nombre]"
  //  - signo -1 (pagó):    "a [nombre]"
  //  - rolAf=intermediario: backend ya forzó contraparte=AF, así que aparece
  //    "de Aloja Fácil" / "a Aloja Fácil" (no el propietario/cliente
  //    original, ver flujo-financiero.md §10.7).
  // Mostramos también un Tag pequeño con el rolAf cuando es intermediario
  // para que admin pueda saber que hubo un tercero original detrás.
  const renderContraparte = (m: MovimientoAseador) => {
    const nombre = m.contraparte?.nombre ?? "—";
    const preposicion = m.signo === 1 ? "de" : "a";
    return (
      <div style={{ fontSize: 12 }}>
        <span style={{ color: "#6b7280" }}>{preposicion} </span>
        <span style={{ fontWeight: 500 }}>{nombre}</span>
        {m.rolAf === "intermediario" && (
          <Tooltip title="AF intermedió este flujo entre dos partes externas">
            <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>
              Intermediario
            </Tag>
          </Tooltip>
        )}
      </div>
    );
  };

  const columns: any[] = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 110,
      render: (v: string | null) => fmtFecha(v),
    },
    {
      title: "Concepto",
      key: "concepto",
      render: (_: any, m: MovimientoAseador) => (
        <Tooltip title={m.descripcion || m.nombre || ""}>
          <div style={{ maxWidth: 260 }}>
            <Tag color={m.signo === 1 ? "blue" : "volcano"}>
              {m.conceptoLabel ?? m.conceptoCodigo ?? "Otro"}
            </Tag>
            {/* Pago recurrente: la misma fila se repite todos los meses que el
                flujo esté vigente. Sin la etiqueta parecería que alguien cargó
                el movimiento N veces. */}
            {m.esRegular && (
              <Tag color="purple" style={{ fontSize: 10 }}>
                Regular
              </Tag>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {m.descripcion || m.nombre || "—"}
            </span>
            {m.notas && <div style={{ fontSize: 12, color: "#6b7280" }}>Notas: {m.notas}</div>}
            <ComprobantesAsignacion movimiento={m} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Contraparte",
      key: "contraparte",
      width: 200,
      render: (_: any, m: MovimientoAseador) => renderContraparte(m),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 130,
      render: (v: MovimientoEstado | "sin_definir") => (
        v === "sin_definir" ? <Tag>Sin indicar</Tag> : <Tag color={MOV_ESTADO_TAG[v]?.color}>{MOV_ESTADO_TAG[v]?.label ?? v}</Tag>
      ),
    },
    {
      title: "Monto",
      key: "monto",
      align: "right" as const,
      width: 120,
      render: (_: any, m: MovimientoAseador) => renderMonto(m),
    },
  ];

  if (isAdmin) {
    columns.push({
      title: "Acciones",
      key: "acciones",
      width: 280,
      render: (_: any, m: MovimientoAseador) => {
        if (m.origen === "lavanderia_asignacion") return <Space size={4} wrap>
          {m.estado !== "pagado" && onApprove && <Button size="small" type="primary" onClick={() => onApprove(m)}>Marcar pagado</Button>}
          {m.estado !== "pendiente" && onRevert && <Button size="small" onClick={() => onRevert(m)}>Marcar pendiente</Button>}
          <Button size="small" href="/lavanderia" target="_blank">Ver lavandería</Button>
        </Space>;
        // Acciones distintas según estado actual.
        if (m.estado === "pendiente") {
          return (
            <Space size={4} wrap>
              {onApprove && (
                <Popconfirm
                  title="Aprobar movimiento"
                  description="Se marcará como pagado con la fecha actual."
                  okText="Aprobar"
                  cancelText="Cancelar"
                  onConfirm={() => onApprove(m)}
                >
                  <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                    Aprobar
                  </Button>
                </Popconfirm>
              )}
              {onDeny && (
                <Popconfirm
                  title="Denegar movimiento"
                  description="Quedará como auditoría, no afectará totales."
                  okText="Denegar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => onDeny(m)}
                >
                  <Button size="small" danger icon={<CloseCircleOutlined />}>
                    Denegar
                  </Button>
                </Popconfirm>
              )}
              {/* Un pago regular no se edita ni se borra desde el ledger: la
                  fila es la proyección mensual de un flujo de /flujos. Editar
                  acá cambiaría TODOS los meses y borrar no tendría destino. */}
              {onEdit && !m.esRegular && (
                <Tooltip title="Editar monto / concepto / fecha">
                  <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(m)} />
                </Tooltip>
              )}
              {onDelete && !m.esRegular && (
                <Popconfirm
                  title="Borrar movimiento"
                  description="Se elimina definitivamente (no es plata entregada todavía)."
                  okText="Borrar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => onDelete(m.id)}
                >
                  <Tooltip title="Borrar movimiento">
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              )}
              {m.esRegular && (
                <Tooltip title="Pago recurrente. Su monto, su día y hasta cuándo rige se editan en Flujos.">
                  <Tag style={{ fontSize: 10 }}>Se edita en Flujos</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        // pagado o denegado: ofrecemos revertir y editar (la edición vuelve
        // automáticamente a 'pendiente' para forzar re-aprobación).
        return (
          <Space size={4} wrap>
            {onRevert && (
              <Popconfirm
                title={
                  m.estado === "pagado"
                    ? "Anular pago"
                    : "Anular denegación"
                }
                description={
                  m.estado === "pagado"
                    ? "Volverá a 'pendiente' y se borra la fecha de pago. Se puede re-aprobar después."
                    : "Volverá a 'pendiente' para re-evaluación."
                }
                okText={m.estado === "pagado" ? "Anular pago" : "Anular"}
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={() => onRevert(m)}
              >
                <Button size="small" danger icon={<UndoOutlined />}>
                  {m.estado === "pagado" ? "Anular pago" : "Anular"}
                </Button>
              </Popconfirm>
            )}
            {onEdit && !m.esRegular && (
              <Tooltip title="Editar (vuelve a pendiente para re-aprobar)">
                <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(m)} />
              </Tooltip>
            )}
            {/* C5 (D-D): un PAGADO no se borra (es plata entregada) → solo
                revertir. Un DENEGADO sí se puede borrar (es auditoría). */}
            {onDelete && !m.esRegular && m.estado === "denegado" && (
              <Popconfirm
                title="Borrar movimiento denegado"
                description="Se elimina definitivamente del historial."
                okText="Borrar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={() => onDelete(m.id)}
              >
                <Tooltip title="Borrar movimiento">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    });
  }

  return (
    <Table<MovimientoAseador>
      // El id de una ocurrencia de flujo regular se repite entre meses (es el
      // id del flujo, no de una fila por mes), así que la clave lleva el
      // período. Sin esto los meses colapsarían en una sola fila.
      rowKey={(m) =>
        m.origen === "lavanderia_asignacion" ? `lavanderia-${m.asignacionId}` : m.esRegular && m.periodo ? `reg-${m.id}-${m.periodo}` : String(m.id)
      }
      size="small"
      dataSource={movimientos}
      columns={columns}
      pagination={false}
      // Filas denegadas se muestran tenues — son referencia.
      rowClassName={(r) => (r.estado === "denegado" ? "fa-row-denied" : "")}
    />
  );
}

// Filtro de estado aplicado a aseos + movimientos. `all` deja pasar todo;
// las claves específicas filtran en ambas tablas. El "no_pagado" agrupa
// pendiente/en_proceso/completado para aseos y pendiente para mov.
type EstadoFiltro = "all" | "no_pagado" | "pagado" | "denegado";

function MesCard({
  mes,
  onApproveMovimiento,
  onDenyMovimiento,
  onRevertMovimiento,
  onEditMovimiento,
  onDeleteMovimiento,
  onPagarAseo,
  onDesmarcarPagoAseo,
  filtroEstado,
}: {
  mes: MesFinanzaAseador;
  onApproveMovimiento?: (mov: MovimientoAseador) => void;
  onDenyMovimiento?: (mov: MovimientoAseador) => void;
  onRevertMovimiento?: (mov: MovimientoAseador) => void;
  onEditMovimiento?: (mov: MovimientoAseador) => void;
  onDeleteMovimiento?: (id: number) => void;
  onPagarAseo?: (cleaningId: number) => void;
  onDesmarcarPagoAseo?: (cleaningId: number) => void;
  filtroEstado: EstadoFiltro;
}) {
  const isAdminAseos = !!(onPagarAseo || onDesmarcarPagoAseo);

  // Aplicar filtro estado a las dos listas. Para aseos, "no_pagado" cubre
  // pendiente/en_proceso/completado (todo lo que no es pagado/denegado).
  // Para movimientos, "no_pagado" mapea a "pendiente".
  const aseosFiltrados = useMemo(() => {
    if (filtroEstado === "all") return mes.aseos;
    if (filtroEstado === "denegado") return []; // aseos no tienen denegado
    if (filtroEstado === "pagado") {
      return mes.aseos.filter((a) => a.estado === "pagado");
    }
    // no_pagado
    return mes.aseos.filter((a) => a.estado !== "pagado");
  }, [mes.aseos, filtroEstado]);

  const movimientosFiltrados = useMemo(() => {
    if (filtroEstado === "all") return mes.movimientos;
    if (filtroEstado === "denegado") {
      return mes.movimientos.filter((m) => m.estado === "denegado");
    }
    if (filtroEstado === "pagado") {
      return mes.movimientos.filter((m) => m.estado === "pagado");
    }
    return mes.movimientos.filter((m) => m.estado === "pendiente");
  }, [mes.movimientos, filtroEstado]);

  const columns: any[] = [
    {
      title: "Fecha",
      dataIndex: "fechaInicio",
      key: "fechaInicio",
      render: (v: string | null) => fmtFecha(v),
      width: 110,
    },
    {
      title: "Propiedad",
      dataIndex: "propiedadNombre",
      key: "propiedadNombre",
      ellipsis: true,
      render: (v: string | null) => formatPropertyName(v) || "—",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 190,
      render: (v: AseoFinanzaItem["estado"], a: AseoFinanzaItem) => (
        <>
          <Tag color={ESTADO_TAG[v]?.color}>{ESTADO_TAG[v]?.label ?? v}</Tag>
          {/* Aseo asignado a futuro (aún no ocurre): pendiente pero no
              cobrable hasta completarse. Round 2026-07-13. */}
          {a.esFuturo && (
            <Tag color="geekblue" style={{ marginInlineStart: 0 }}>
              Programado
            </Tag>
          )}
        </>
      ),
    },
    {
      title: "Sueldo",
      dataIndex: "sueldo",
      key: "sueldo",
      align: "right" as const,
      width: 110,
      // Round 2026-08-25 — aseo con equipo 1..N: el ledger muestra LA PARTE
      // de este aseador, y el tag "Compartido" explica que el sueldo total
      // del aseo se repartió entre varias.
      render: (v: number, a: any) => (
        <>
          {a?.aseoCompartido && (
            <Tooltip
              title={`Aseo compartido entre ${a.totalAseadoras} aseadoras — sueldo total del aseo: ${fmtClp(a.sueldoTotalAseo ?? 0)}. Este monto es la parte de este aseador.`}
            >
              <Tag color="purple" style={{ marginInlineEnd: 6 }}>
                Compartido
              </Tag>
            </Tooltip>
          )}
          {fmtClp(v)}
        </>
      ),
    },
    // Round 2026-05-11: las columnas "Bono" y "Descuento" se removieron.
    // Los flujos extra (bono, multa, adelanto, propina, reembolso) ahora
    // viven como `movimientos` en una tabla separada del mismo panel.
    {
      title: "Neto",
      dataIndex: "neto",
      key: "neto",
      align: "right" as const,
      width: 130,
      render: (v: number) => <strong>{fmtClp(v)}</strong>,
    },
  ];

  // Round 2026-04-30 quinquies — columna Acciones para que admin pague
  // un aseo individual desde acá (o desmarque el pago si fue por error).
  // Solo aparece en la vista admin (cuando vienen los callbacks).
  if (isAdminAseos) {
    columns.push({
      title: "Acciones",
      key: "acciones",
      width: 200,
      render: (_: any, a: AseoFinanzaItem) => {
        if (a.estado === "completado" && onPagarAseo) {
          return (
            <Popconfirm
              title="Marcar aseo como pagado"
              description={`Se registrará la fecha de pago como hoy. Neto: ${fmtClp(a.neto)}.`}
              okText="Marcar pagado"
              cancelText="Cancelar"
              onConfirm={() => onPagarAseo(a.id)}
            >
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                Marcar pagado
              </Button>
            </Popconfirm>
          );
        }
        if (a.estado === "pagado" && onDesmarcarPagoAseo) {
          return (
            <Popconfirm
              title="Anular pago del aseo"
              description="Volverá a 'completado (por pagar)' y se borrará la fecha de pago."
              okText="Anular pago"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => onDesmarcarPagoAseo(a.id)}
            >
              <Button size="small" danger icon={<UndoOutlined />}>
                Anular pago
              </Button>
            </Popconfirm>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    });
  }

  // El total general del mes incluye aseos + movimientos extra. Es lo que
  // el aseador debería usar como "guía" del mes (vs `totalNeto` que es solo
  // aseos). Si no hay movimientos, ambos coinciden.
  return (
    <div className="fa-mes">
      <div className="fa-mes-summary">
        <div className="fa-mes-stat">
          <span className="fa-stat-label">Total general (neto)</span>
          <strong className="fa-stat-value fa-stat-neto">
            {fmtClp(mes.totalGeneralNeto)}
          </strong>
        </div>
        <div className="fa-mes-stat">
          <span className="fa-stat-label">Ya pagado</span>
          <span className="fa-stat-value fa-stat-pagado">
            {fmtClp(mes.totalGeneralPagado)}
          </span>
        </div>
        <div className="fa-mes-stat">
          <span className="fa-stat-label">Pendiente</span>
          <span className="fa-stat-value fa-stat-pendiente">
            {fmtClp(mes.totalGeneralPendiente)}
          </span>
        </div>
        <div className="fa-mes-stat">
          <Tooltip title="Incluido en el neto; aún no se indicó si está pendiente o pagado."><span className="fa-stat-label">Sin estado</span></Tooltip>
          <span className="fa-stat-value">{fmtClp(mes.totalMovimientosSinEstado || 0)}</span>
        </div>
        <div className="fa-mes-stat">
          <span className="fa-stat-label">Aseos</span>
          <span className="fa-stat-value">{mes.cantidadAseos}</span>
        </div>
        {/* Round 2026-05-11: stats "Bonos" y "Descuentos" se removieron del
            resumen del mes. Los bonos/descuentos ahora viven como movimientos
            extra (sección "Movimientos +/−" abajo si hay alguno). */}
        {mes.cantidadMovimientos > 0 && (
          <>
            <div className="fa-mes-stat">
              <span className="fa-stat-label">Movimientos +</span>
              <span className="fa-stat-value" style={{ color: "#16a34a" }}>
                +{fmtClp(mes.totalMovimientosRecibidos)}
              </span>
            </div>
            <div className="fa-mes-stat">
              <span className="fa-stat-label">Movimientos −</span>
              <span className="fa-stat-value" style={{ color: "#dc2626" }}>
                -{fmtClp(mes.totalMovimientosEmitidos)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ─── Aseos ──────────────────────────────────────────────────
          Round 2026-04-30 quater — el label antes era "del mes"/"este mes"
          fijo, lo que confundía al admin viendo bucket SEMANAL ("dice 'mes'
          pero estoy viendo una semana"). Detectamos el modo por la presencia
          de `mes.weekStart` (lunes ISO del bucket): si está, es semana. */}
      <div className="fa-mes-section-title">
        <CalendarOutlined /> Aseos {(mes as any).weekStart ? "de la semana" : "del mes"} ({aseosFiltrados.length}/{mes.cantidadAseos})
      </div>
      {aseosFiltrados.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            mes.cantidadAseos === 0
              ? (mes as any).weekStart
                ? "Sin aseos esta semana"
                : "Sin aseos este mes"
              : "Ningún aseo coincide con el filtro de estado"
          }
        />
      ) : (
        <Table<AseoFinanzaItem>
          rowKey="id"
          size="small"
          dataSource={aseosFiltrados}
          columns={columns}
          pagination={false}
        />
      )}

      {/* ─── Movimientos extra (Fase 2) ─────────────────────────────
          Round 2026-04-30 quater — antes solo se mostraba la sección si
          cantidadMovimientos > 0. Ahora la mostramos SIEMPRE para que el
          admin sepa que hay un slot dedicado a "Movimientos extra"
          (sueldos al aseador, adelantos, multas, reembolsos) y no se
          confunda viendo "0 aseos" pensando que el panel está roto.
          Si el aseador no tuvo movimientos extra en el bucket, el Empty
          deja claro que NO hay nada que mostrar (vs. "está oculto"). */}
      <div className="fa-mes-section-title" style={{ marginTop: 18 }}>
        <WalletOutlined /> Movimientos extra ({movimientosFiltrados.length}/{mes.cantidadMovimientos})
        {mes.cantidadPendientes > 0 && (
          <Tag color="gold" style={{ marginLeft: 8, fontWeight: 500 }}>
            {mes.cantidadPendientes} pendiente
            {mes.cantidadPendientes === 1 ? "" : "s"} de aprobación
          </Tag>
        )}
      </div>
      {movimientosFiltrados.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            mes.cantidadMovimientos === 0
              ? (mes as any).weekStart
                ? "Sin movimientos extra esta semana (sueldos, adelantos, bonos, multas, reembolsos)"
                : "Sin movimientos extra este mes"
              : "Ningún movimiento coincide con el filtro de estado"
          }
        />
      ) : (
        <MovimientosTable
          movimientos={movimientosFiltrados}
          onApprove={onApproveMovimiento}
          onDeny={onDenyMovimiento}
          onRevert={onRevertMovimiento}
          onEdit={onEditMovimiento}
          onDelete={onDeleteMovimiento}
        />
      )}
    </div>
  );
}

export interface FinanzasAseadorViewProps {
  /** Meses ya cargados desde el backend (orden: más reciente arriba). */
  meses: MesFinanzaAseador[];
  /** Cantidad de meses solicitada (para el toggle 3/6/12). */
  months: number;
  /** Indica que el padre está cargando datos. */
  loading: boolean;
  /** Mensaje de error si lo hubo (con botón "Reintentar"). */
  error: string | null;
  /** Cambiar el rango de meses (3/6/12). */
  onChangeMonths: (m: number) => void;
  /** Forzar refetch (botón "Actualizar"). */
  onReload: () => void;
  /**
   * Cuando es `true`, el panel oculta el banner "Próximo pago": ese banner
   * habla en primera persona ("a recibir") y solo tiene sentido cuando el
   * usuario es el propio aseador. La vista admin lo reemplaza por un texto
   * neutro abajo del header (responsabilidad del padre).
   */
  hideProximoPago?: boolean;
  /**
   * Acciones admin sobre movimientos extra (Fase 2). Cuando vienen
   * definidas, la tabla de movimientos pinta una columna "Acciones" con
   * botones aprobar/denegar para cada fila pendiente. La vista del aseador
   * las omite (read-only por contrato).
   *
   * Reciben el MOVIMIENTO completo (round 2026-07-27), no su id: una
   * ocurrencia de flujo regular se identifica con el par (id, período) porque
   * el regular es una sola fila que vale por N meses.
   */
  onApproveMovimiento?: (mov: MovimientoAseador) => void;
  onDenyMovimiento?: (mov: MovimientoAseador) => void;
  /**
   * Round 2026-04-30 quinquies — admin puede revertir aprobación/denegación
   * (vuelve a 'pendiente') y editar movimientos existentes. Cuando vienen
   * definidos, la columna "Acciones" pinta los botones correspondientes
   * según el estado actual del row.
   */
  onRevertMovimiento?: (mov: MovimientoAseador) => void;
  onEditMovimiento?: (mov: MovimientoAseador) => void;
  /**
   * Round 2026-06-20 (C5, D-D) — admin borra un movimiento. Solo se ofrece
   * para movimientos pendientes o denegados (un pagado se corrige con
   * revertir, no se borra).
   */
  onDeleteMovimiento?: (movimientoId: number) => void;
  /**
   * Round 2026-04-30 quinquies — admin paga / desmarca un aseo individual
   * desde la tabla de aseos. Equivalente a `pagarTodo` pero por fila. Si
   * vienen definidos, la tabla de aseos pinta columna "Acciones".
   */
  onPagarAseo?: (cleaningId: number) => void;
  onDesmarcarPagoAseo?: (cleaningId: number) => void;
  /**
   * Slot opcional al lado de la toolbar — la vista admin lo usa para
   * inyectar el botón "Registrar movimiento". El aseador no lo usa.
   */
  toolbarExtra?: React.ReactNode;
  /**
   * Cuando `true`, oculta el Segmented interno 3/6/12 meses. La vista admin
   * (`/finanzas-aseador-admin`) lo usa porque maneja su propio toggle (que
   * además soporta modo "Semana"). En la vista del aseador queda visible.
   */
  hideRangeSelector?: boolean;
  /**
   * Modo "mes específico" (round 2026-07-06) — solo la vista del aseador. Si
   * `onSelectMonth` viene definido, se muestra un selector de mes/año junto al
   * Segmented: al elegir un mes, el padre pide ese rango puntual. `null` = modo
   * "últimos N meses" (el Segmented).
   */
  selectedMonth?: Dayjs | null;
  onSelectMonth?: (m: Dayjs | null) => void;
  /**
   * Modo SEMANAS (round 2026-07-06) — la vista del aseador ("Mis ingresos")
   * usa buckets semanales L-D en vez de meses (los pagos son semanales, los
   * lunes). Si `onChangeWeeks` viene definido, el selector de rango muestra
   * semanas en vez de meses. `groupByMonth` agrupa esas semanas bajo un
   * encabezado de mes (los buckets ya traen `year`/`month`/`weekStart`).
   */
  weeks?: number;
  onChangeWeeks?: (w: number) => void;
  groupByMonth?: boolean;
  /**
   * Caso "Sin aseador designado" (round 2026-07-12, solo `/finanzas-aseador-admin`):
   * muestra un banner aclaratorio de que son aseos abiertos (id_usuario NULL),
   * SOLO LECTURA. El padre además NO pasa los callbacks de pago/aprobación, así
   * que esas acciones no aparecen. NO afecta la vista del propio aseador.
   */
  sinAseador?: boolean;
}

/**
 * Renderiza el panel de finanzas del aseador con header de KPIs, próximo
 * pago (opcional), y un Collapse por mes. Componente "tonto" — el padre
 * decide qué datos pasarle.
 */
export default function FinanzasAseadorView({
  meses,
  months,
  loading,
  error,
  onChangeMonths,
  onReload,
  hideProximoPago = false,
  onApproveMovimiento,
  onDenyMovimiento,
  onRevertMovimiento,
  onEditMovimiento,
  onDeleteMovimiento,
  onPagarAseo,
  onDesmarcarPagoAseo,
  toolbarExtra,
  hideRangeSelector = false,
  selectedMonth = null,
  onSelectMonth,
  weeks = 12,
  onChangeWeeks,
  groupByMonth = false,
  sinAseador = false,
}: FinanzasAseadorViewProps) {
  // Round 2026-04-30 quinquies — filtro de estado que aplica a aseos +
  // movimientos en TODOS los buckets visibles. "Todos" deja pasar; "No
  // pagado" cubre pendiente/en_proceso/completado (aseos) + pendiente
  // (movimientos); "Pagado" y "Denegado" son obvios. Combina con el rango
  // del toolbar (admin elige semana/mes y luego refina por estado).
  const [filtroEstado, setFiltroEstado] = useState<EstadoFiltro>("all");
  // Total acumulado del rango visible — útil para tener una idea global.
  // Usamos `totalGeneral*` (aseos + movimientos) en lugar de solo aseos
  // para que el header refleje el ledger completo desde Fase 2.
  const totalRango = useMemo(
    () => meses.reduce((acc, m) => acc + m.totalGeneralNeto, 0),
    [meses],
  );
  const totalPagadoRango = useMemo(
    () => meses.reduce((acc, m) => acc + m.totalGeneralPagado, 0),
    [meses],
  );
  const totalPendienteRango = useMemo(
    () => meses.reduce((acc, m) => acc + m.totalGeneralPendiente, 0),
    [meses],
  );
  const totalSinEstadoRango = useMemo(
    () => meses.reduce((acc, m) => acc + (m.totalMovimientosSinEstado || 0), 0),
    [meses],
  );
  const cantidadPendientesRango = useMemo(
    () => meses.reduce((acc, m) => acc + m.cantidadPendientes, 0),
    [meses],
  );

  /**
   * Próximo pago: regla del sistema (ver docs/flujo-financiero.md §6 y
   * docs/rol-aseador.md §10): los pagos se hacen los **lunes**, cubriendo
   * todos los flujos de la semana **anterior** (Lun → Dom). El lunes
   * mismo en que se paga NO entra (corresponde al lunes siguiente).
   *
   * Round 2026-04-30 octies — el cálculo ahora suma TODOS los flujos del
   * aseador en el rango (aseos + movimientos extra), con signo. Antes
   * solo sumaba aseos `neto` y dejaba afuera adelantos, propinas, multas
   * y descuentos disciplinarios — el banner mostraba un monto
   * sistemáticamente menor (o mayor) al real.
   *
   * Reglas de inclusión:
   *  - Aseos con `fechaInicio ∈ [lunesPasado, domingoPasado]` y
   *    `estado != pagado` → suma `+neto`.
   *  - Movimientos con `fecha ∈ [lunesPasado, domingoPasado]` y
   *    `estado == pendiente` → suma `signo · monto`.
   *    Recibidos (signo=+1) suman positivo, emitidos (signo=-1) restan.
   *  - Pagados: ya están liquidados.
   *  - Denegados: NO entran a totales (auditoría).
   */
  const proximoPago = useMemo(() => {
    const hoy = ahoraChile();
    const dow = hoy.day(); // 0=dom, 1=lun, ..., 6=sab
    // Días al próximo lunes (incluye hoy si es lunes).
    const diasAlProxLunes = dow === 1 ? 0 : (8 - dow) % 7;
    const proxLunes = hoy.add(diasAlProxLunes, "day").startOf("day");
    const lunesPasado = proxLunes.subtract(7, "day"); // semana cubierta: lunes
    const domingoPasado = proxLunes.subtract(1, "day"); // semana cubierta: domingo

    const enRango = (f: dayjs.Dayjs) =>
      (f.isSame(lunesPasado, "day") || f.isAfter(lunesPasado, "day")) &&
      (f.isSame(domingoPasado, "day") || f.isBefore(domingoPasado, "day"));

    let monto = 0;
    let cantidad = 0;
    for (const mes of meses) {
      // Aseos no pagados de la semana anterior.
      for (const a of mes.aseos) {
        if (!a.fechaInicio) continue;
        const f = dayjs(a.fechaInicio);
        if (enRango(f) && a.estado !== "pagado") {
          monto += a.neto;
          cantidad += 1;
        }
      }
      // Movimientos extra de la semana anterior — pendientes contribuyen
      // al pago, pagados ya fueron, denegados no cuentan.
      for (const m of mes.movimientos) {
        if (!m.fecha) continue;
        const f = dayjs(m.fecha);
        if (
          enRango(f) &&
          m.estado === "pendiente"
        ) {
          monto += m.signo * m.monto;
          cantidad += 1;
        }
      }
    }

    return {
      fechaPago: proxLunes,
      lunesPasado,
      domingoPasado,
      monto,
      cantidad,
      esHoy: diasAlProxLunes === 0,
    };
  }, [meses]);

  // Un panel del Collapse por bucket (semana o mes). Extraído para reusarlo en
  // el render plano y en el agrupado por mes (round 2026-07-06).
  const buildPanelItem = (m: MesFinanzaAseador) => ({
    key: m.weekStart || `${m.year}-${m.month}`,
    label: (
      <div className="fa-mes-header">
        <Title level={5} style={{ margin: 0 }}>
          {capitalizarMes(m.label)}
        </Title>
        <span className="fa-mes-header-totals">
          <Tag color="blue">
            {m.cantidadAseos} {m.cantidadAseos === 1 ? "aseo" : "aseos"}
          </Tag>
          {m.cantidadMovimientos > 0 && (
            <Tag color="purple">{m.cantidadMovimientos} mov.</Tag>
          )}
          {m.cantidadPendientes > 0 && (
            <Tag color="gold">{m.cantidadPendientes} pend.</Tag>
          )}
          <strong>{fmtClp(m.totalGeneralNeto)}</strong>
        </span>
      </div>
    ),
    children: (
      <MesCard
        mes={m}
        onApproveMovimiento={onApproveMovimiento}
        onDenyMovimiento={onDenyMovimiento}
        onRevertMovimiento={onRevertMovimiento}
        onEditMovimiento={onEditMovimiento}
        onDeleteMovimiento={onDeleteMovimiento}
        onPagarAseo={onPagarAseo}
        onDesmarcarPagoAseo={onDesmarcarPagoAseo}
        filtroEstado={filtroEstado}
      />
    ),
  });

  const MESES_ES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];

  // Agrupa los buckets-semana por (año, mes) → encabezado de mes + Collapse de
  // sus semanas. Preserva el orden que trae `meses` (más reciente arriba).
  const renderGroupedByMonth = () => {
    /** @type {{ key: string; year: number; month: number; weeks: MesFinanzaAseador[] }[]} */
    const groups: {
      key: string;
      year: number;
      month: number;
      weeks: MesFinanzaAseador[];
    }[] = [];
    const byKey = new Map<string, (typeof groups)[number]>();
    for (const m of meses) {
      const gk = `${m.year}-${m.month}`;
      let g = byKey.get(gk);
      if (!g) {
        g = { key: gk, year: m.year, month: m.month, weeks: [] };
        byKey.set(gk, g);
        groups.push(g);
      }
      g.weeks.push(m);
    }
    return (
      <div className="fa-month-groups">
        {groups.map((g, gi) => (
          <div
            key={g.key}
            className="fa-month-group"
            style={{ marginBottom: 18 }}
          >
            <Title level={5} style={{ margin: "4px 0 8px" }}>
              {MESES_ES[(((g.month ?? 1) - 1) % 12 + 12) % 12]} {g.year}
            </Title>
            <Collapse
              className="fa-collapse"
              defaultActiveKey={
                gi === 0 && g.weeks[0]
                  ? [g.weeks[0].weekStart || g.key]
                  : []
              }
              expandIconPosition="end"
              items={g.weeks.map(buildPanelItem)}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {sinAseador && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Aseos sin aseador designado (abiertos)"
          description={
            <span style={{ fontSize: 13 }}>
              Estos aseos tienen <code>id_usuario</code> vacío (nadie asignado
              todavía). Se listan para que sepas qué trabajo está{" "}
              <b>sin asignar</b> y su sueldo presupuestado. Es{" "}
              <b>solo lectura</b>: no son pagables ni tienen movimientos hasta
              que un aseador los tome (asígnalos desde{" "}
              <a href="/aseos">/aseos</a> o <a href="/calendario">/calendario</a>
              ). Los montos NO son una deuda a nadie — ya se cuentan en la
              utilidad AF por separado.
            </span>
          }
        />
      )}
      <div className="fa-toolbar">
        {!hideRangeSelector &&
          (onChangeWeeks ? (
            // Vista del aseador: rango en SEMANAS (los pagos son semanales L-D).
            // Si hay un mes puntual elegido, deseleccionamos el Segmented para
            // no confundir (el MonthPicker manda mientras esté activo).
            <Segmented
              value={selectedMonth ? ("" as unknown as number) : weeks}
              onChange={(v) => onChangeWeeks(Number(v))}
              options={[
                { label: "8 semanas", value: 8 },
                { label: "12 semanas", value: 12 },
                { label: "24 semanas", value: 24 },
              ]}
            />
          ) : (
            <Segmented
              // En modo "mes específico" el Segmented no representa lo que se ve;
              // lo dejamos sin selección para no confundir (el MonthPicker manda).
              value={selectedMonth ? ("" as unknown as number) : months}
              onChange={(v) => onChangeMonths(Number(v))}
              options={[
                { label: "3 meses", value: 3 },
                { label: "6 meses", value: 6 },
                { label: "12 meses", value: 12 },
              ]}
            />
          ))}
        {!hideRangeSelector && onSelectMonth && (
          <Tooltip title="Ver las finanzas de un mes y año puntual (incluye meses futuros para revisar aseos programados)">
            <DatePicker
              picker="month"
              placeholder="Mes específico"
              value={selectedMonth}
              onChange={(m) => onSelectMonth(m ?? null)}
              allowClear
              format="MMMM [de] YYYY"
              // Sin restricción de fecha: se permiten meses FUTUROS para ver
              // los aseos ya asignados/programados de meses lejanos (el modo
              // "semanas" solo llega hasta la próxima semana). Round 2026-07-13.
            />
          </Tooltip>
        )}
        <Button
          icon={<ReloadOutlined />}
          onClick={onReload}
          loading={loading}
        >
          Actualizar
        </Button>
        {toolbarExtra}
      </div>

      {/* Filtro por estado — round 2026-04-30 quinquies. Aplica a aseos
          + movimientos en TODOS los buckets visibles. Combinado con el
          rango (semana/mes/N meses) permite a admin enfocar exactamente
          lo que necesita aprobar / revisar. Los chips llevan badge con
          el conteo del rango completo. */}
      <div
        className="fa-toolbar"
        style={{ marginTop: 6, alignItems: "center" }}
      >
        <Text strong style={{ fontSize: 13 }}>
          Estado:
        </Text>
        <Segmented
          value={filtroEstado}
          onChange={(v) => setFiltroEstado(v as EstadoFiltro)}
          options={[
            { label: "Todos", value: "all" },
            {
              label: `Pendientes${cantidadPendientesRango > 0 ? ` (${cantidadPendientesRango})` : ""}`,
              value: "no_pagado",
            },
            { label: "Pagados", value: "pagado" },
            { label: "Denegados", value: "denegado" },
          ]}
        />
      </div>

      {/* Próximo pago — banner llamativo. Los pagos van los lunes y
          cubren la semana anterior completa (Lun-Dom). El lunes mismo
          del pago no entra (se paga al lunes siguiente). El admin no
          ve este bloque porque no es "a recibir" para él. */}
      {!hideProximoPago && (
        <Alert
          banner={proximoPago.esHoy}
          type={proximoPago.esHoy ? "success" : "info"}
          showIcon
          icon={<GiftOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Próximo pago
                </div>
                <strong style={{ fontSize: 18 }}>
                  {proximoPago.esHoy ? "Hoy" : "Lunes"} {proximoPago.fechaPago.format("DD-MM-YYYY")}
                </strong>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Cubre
                </div>
                <span style={{ fontSize: 14 }}>
                  Lun {proximoPago.lunesPasado.format("DD-MM")} → Dom {proximoPago.domingoPasado.format("DD-MM")}
                </span>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  A recibir
                </div>
                <strong style={{ fontSize: 18, color: "#7c3aed" }}>
                  {fmtClp(proximoPago.monto)}
                </strong>
                <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>
                  ({proximoPago.cantidad} {proximoPago.cantidad === 1 ? "aseo" : "aseos"})
                </span>
              </div>
            </div>
          }
          description={
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Los pagos se hacen los lunes y cubren los aseos completados de la semana anterior (Lun-Dom). El lunes mismo del pago no entra: se liquida el lunes siguiente.
            </span>
          }
        />
      )}

      {/* Resumen total del rango */}
      <div className="fa-rango-summary">
        <div className="fa-rango-card">
          <DollarOutlined className="fa-rango-icon" />
          <div>
            <div className="fa-rango-label">Total neto del rango</div>
            <strong className="fa-rango-value">{fmtClp(totalRango)}</strong>
          </div>
        </div>
        <div className="fa-rango-card">
          <CalendarOutlined className="fa-rango-icon fa-rango-icon-pagado" />
          <div>
            <div className="fa-rango-label">Pagado</div>
            <strong className="fa-rango-value fa-stat-pagado">
              {fmtClp(totalPagadoRango)}
            </strong>
          </div>
        </div>
        <div className="fa-rango-card">
          <CalendarOutlined className="fa-rango-icon fa-rango-icon-pendiente" />
          <div>
            <div className="fa-rango-label">Pendiente</div>
            <strong className="fa-rango-value fa-stat-pendiente">
              {fmtClp(totalPendienteRango)}
            </strong>
          </div>
        </div>
        <div className="fa-rango-stat"><div><Tooltip title="Incluido en el neto; aún no se indicó si está pendiente o pagado."><div className="fa-rango-label">Sin estado</div></Tooltip><strong className="fa-rango-value">{fmtClp(totalSinEstadoRango)}</strong></div></div>
      </div>

      {/* Alerta para admin: movimientos extra esperando aprobación. Para
          el aseador es informativo (sus pagos pendientes); para el admin
          es accionable (ver tabs/buttons de aprobar). Solo aparece si > 0. */}
      {cantidadPendientesRango > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WalletOutlined />}
          style={{ marginBottom: 12 }}
          message={
            <span>
              <strong>{cantidadPendientesRango}</strong> movimiento
              {cantidadPendientesRango === 1 ? "" : "s"} pendiente
              {cantidadPendientesRango === 1 ? "" : "s"} de aprobación en este rango.
            </span>
          }
          description={
            onApproveMovimiento
              ? "Abre cada mes y aprueba o deniega los movimientos en estado 'Pendiente'."
              : "Quedan en pendiente hasta que admin los apruebe (lunes)."
          }
        />
      )}

      {error ? (
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          action={
            <Button size="small" onClick={onReload}>
              Reintentar
            </Button>
          }
        />
      ) : null}

      {loading ? (
        <div className="fa-loading">
          <Spin size="large" />
        </div>
      ) : meses.length === 0 ? (
        <Empty description="Sin datos en el rango seleccionado." />
      ) : groupByMonth ? (
        // Vista del aseador: semanas L-D agrupadas bajo un encabezado de mes.
        renderGroupedByMonth()
      ) : (
        <Collapse
          className="fa-collapse"
          // En modo semanas, `weekStart` (YYYY-MM-DD del lunes) garantiza
          // keys únicos cuando hay dos semanas dentro del mismo mes. En
          // modo meses, `weekStart` es undefined y caemos al year-month.
          defaultActiveKey={[
            meses[0].weekStart || `${meses[0].year}-${meses[0].month}`,
          ]}
          expandIconPosition="end"
          items={meses.map(buildPanelItem)}
        />
      )}
    </>
  );
}
